import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { Container, Row, Col, Button, Spinner, Form, OverlayTrigger, Tooltip, Badge } from 'react-bootstrap'
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import SearchInputWithClear from '../components/SearchInputWithClear'
import CopyableText from '../components/CopyableText'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly, formatCurrency } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { createColumnToggleDropdown, getReportColumns, useTableState } from '../utils/tableUtils'

const fmtDec = (val, digits = 2) => {
    if (val === null || val === undefined) return ''
    return Number(val).toFixed(digits)
}

const fmtPct = (val) => {
    if (val === null || val === undefined) return ''
    return (Number(val) * 100).toFixed(1) + '%'
}

const fmtInt = (val) => {
    if (val === null || val === undefined) return ''
    return Number(val).toLocaleString()
}

function AmazonASINReportDetailScreen() {
    const { reportId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [returnFilters] = useState(location.state?.returnFilters || '')

    const [report, setReport] = useState(null)
    const [reportLoading, setReportLoading] = useState(true)
    const [error, setError] = useState('')

    const [rows, setRows] = useState([])
    const [rowsLoading, setRowsLoading] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [searchInputValue, setSearchInputValue] = useState('')
    const debounceTimeoutRef = useRef(null)
    const [sortColumn, setSortColumn] = useState('revenue')
    const [sortDirection, setSortDirection] = useState('desc')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [excludeNoSales, setExcludeNoSales] = useState(true)
    const [exporting, setExporting] = useState(false)

    const initialTableState = {
        visibility: {
            id: false,
            asin: true, product_name: true, amazon_sku: true, sale_price: true,
            fba_fee: true, inbound_fee: true, referral_fee: true,
            storage_fee: true, awd_fee: true, ad_spend_per_unit: true,
            work_cost: true, cogs: true, profit: true,
            contribution_margin: true, break_even_acos: true, max_profitable_cpa: true,
            return_on_cost: true,
            units_sold: true, units_returned: true, revenue: true,
            ad_spend_total: true, ad_sales: true, ad_impressions: true, ad_clicks: true,
            acos: true, ad_conversion_rate: true,
            total_conversion_rate: true, sessions: true,
            share_sales_by_ad: true, ad_cost_share_of_price: true,
            available_qty: true, total_fba_qty: true, month_supply: true, your_price: true,
            fba_sale_price: true, inventory_health_status: true, inventory_level: true,
            low_inventory_fee: false,
        },
        widths: {}
    }
    const { columnState, updateColumnVisibility } = useTableState('amazon_asin_report', initialTableState)
    const columnVisibility = columnState.visibility || initialTableState.visibility
    const toggleColumnVisibility = (columnId) => updateColumnVisibility(columnId, !columnVisibility[columnId])
    const columnLabels = {
        id: 'ID', asin: 'ASIN', product_name: 'Name', amazon_sku: 'SKU',
        sale_price: 'Sale Price', fba_fee: 'FBA Fee', inbound_fee: 'Inbound Fee',
        referral_fee: 'Amazon Fees', storage_fee: 'Storage Fee', awd_fee: 'AWD Fees',
        ad_spend_per_unit: 'ADV for Month', work_cost: 'Work Cost', cogs: 'COGS',
        profit: 'Profit',
        contribution_margin: 'Margin (Pre-Ad)', break_even_acos: 'BE ACoS', max_profitable_cpa: 'Max CPA',
        return_on_cost: 'Return on Cost',
        units_sold: 'Units Sold', units_returned: 'Units Returned', revenue: 'Revenue',
        ad_spend_total: 'Spend', ad_sales: 'Sales (Ad)',
        ad_impressions: 'Impressions', ad_clicks: 'Clicks', acos: 'ACOS',
        ad_conversion_rate: 'Conversion (Ad)',
        total_conversion_rate: 'Conversion (Total)', sessions: 'Sessions',
        share_sales_by_ad: 'Share Sales by Ad', ad_cost_share_of_price: 'Ad Cost in Price %',
        available_qty: 'Available', total_fba_qty: 'Inventory at FBA',
        month_supply: 'Month Supply', your_price: 'Your Price',
        fba_sale_price: 'Sale Price (FBA)', inventory_health_status: 'FBA Inventory Status',
        inventory_level: 'Inventory Level', low_inventory_fee: 'Low Inventory Fee',
    }

    const MONTHLY_HIDDEN_COLUMNS = [
        'ad_spend_total', 'ad_spend_per_unit', 'ad_sales', 'ad_impressions',
        'ad_clicks', 'acos', 'ad_conversion_rate', 'share_sales_by_ad',
        'ad_cost_share_of_price', 'low_inventory_fee',
    ]

    useEffect(() => {
        if (report && report.report_type === 'monthly') {
            MONTHLY_HIDDEN_COLUMNS.forEach(col => {
                if (columnVisibility[col] !== false) {
                    updateColumnVisibility(col, false)
                }
            })
            if (!columnVisibility['inventory_level']) {
                updateColumnVisibility('inventory_level', true)
            }
        }
    }, [report?.id, report?.report_type])

    const fetchReport = useCallback(async () => {
        setReportLoading(true)
        try {
            const params = excludeNoSales ? { exclude_no_sales: 'true' } : {}
            const data = await amazonAPI.asinReports.get(reportId, params)
            setReport(data)
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setReportLoading(false)
        }
    }, [reportId, excludeNoSales])

    useEffect(() => {
        fetchReport()
    }, [fetchReport])

    const fetchRows = useCallback(async () => {
        if (!report || report.status !== 'DONE') return
        setRowsLoading(true)
        setError('')
        try {
            const params = new URLSearchParams({
                page: currentPage,
                page_size: pageSize,
                sortField: sortColumn,
                sortDirection: sortDirection,
            })
            if (searchTerm) params.set('search', searchTerm)
            if (excludeNoSales) params.set('exclude_no_sales', 'true')

            const data = await amazonAPI.asinReports.getRows(report.id, params.toString())
            if (data) {
                setRows(data.results || [])
                setTotalItems(data.count || 0)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setRowsLoading(false)
        }
    }, [report, currentPage, pageSize, sortColumn, sortDirection, searchTerm, excludeNoSales])

    useEffect(() => {
        fetchRows()
    }, [fetchRows])

    const handleSearch = (e) => {
        const value = e?.target?.value ?? e
        setSearchInputValue(value)
        if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current)
        debounceTimeoutRef.current = setTimeout(() => {
            setSearchTerm(value)
            setCurrentPage(1)
        }, 300)
    }

    const handleClearSearch = () => {
        setSearchInputValue('')
        setSearchTerm('')
        setCurrentPage(1)
    }

    const handleExport = async () => {
        setExporting(true)
        setError('')
        try {
            const columns = getReportColumns('amazon_asin_report', columnVisibility)
            await amazonAPI.asinReports.exportExcel(reportId, {
                columns,
                exclude_no_sales: excludeNoSales,
                search: searchTerm,
                sortField: sortColumn,
                sortDirection: sortDirection,
            })
        } catch (err) { setError(formatErrorResponse(err)) }
        finally { setExporting(false) }
    }

    const handleSort = (column, direction) => {
        setSortColumn(column)
        setSortDirection(direction)
        setCurrentPage(1)
    }

    const reportColumns = [
        { id: 'id', header: 'ID',
          cell: (row) => row.id },
        { id: 'asin', header: 'ASIN', sortable: true,
          cell: (row) => <CopyableText value={row.asin} toastLabel="ASIN" searchTerm={searchTerm} /> },
        { id: 'product_name', header: 'Name', sortable: true,
          cell: (row) => row.product_name || '' },
        { id: 'amazon_sku', header: 'SKU', sortable: true,
          cell: (row) => row.amazon_sku || '' },
        { id: 'sale_price', header: 'Sale Price', sortable: true,
          cell: (row) => row.sale_price ? `$${fmtDec(row.sale_price)}` : '' },
        { id: 'fba_fee', header: 'FBA Fee', sortable: true,
          cell: (row) => row.fba_fee ? `$${fmtDec(row.fba_fee)}` : '' },
        { id: 'inbound_fee', header: 'Inbound Fee', sortable: true,
          cell: (row) => row.inbound_fee ? `$${fmtDec(row.inbound_fee)}` : '' },
        { id: 'referral_fee', header: 'Amazon Fees', sortable: true,
          cell: (row) => row.referral_fee ? `$${fmtDec(row.referral_fee)}` : '' },
        { id: 'storage_fee', header: 'Storage Fee', sortable: true,
          cell: (row) => row.storage_fee ? `$${fmtDec(row.storage_fee)}` : '' },
        { id: 'awd_fee', header: 'AWD Fees', sortable: true,
          cell: (row) => row.awd_fee ? `$${fmtDec(row.awd_fee)}` : '' },
        { id: 'ad_spend_per_unit', header: 'ADV for Month', sortable: true,
          cell: (row) => row.ad_spend_per_unit ? `$${fmtDec(row.ad_spend_per_unit)}` : '' },
        { id: 'work_cost', header: 'Work Cost',
          cell: () => <span className="text-muted">TBD</span> },
        { id: 'cogs', header: 'COGS', sortable: true,
          cell: (row) => {
              if (row.cogs === null || row.cogs === undefined || Number(row.cogs) === 0) {
                  return row.units_sold > 0
                      ? <i className="fas fa-exclamation-triangle text-warning" title="COGS missing" style={{ fontSize: '0.75rem' }}></i>
                      : ''
              }
              return `$${fmtDec(row.cogs)}`
          }},
        { id: 'profit', header: 'Profit', sortable: true,
          cell: (row) => {
              if (row.profit === null || row.profit === undefined) return ''
              const val = Number(row.profit)
              const color = val >= 0 ? 'text-success' : 'text-danger'
              return <span className={color}>${fmtDec(val)}</span>
          }},
        { id: 'contribution_margin', header: 'Margin (Pre-Ad)', sortable: true,
          cell: (row) => {
              if (row.contribution_margin === null || row.contribution_margin === undefined) return ''
              const val = Number(row.contribution_margin)
              const color = val >= 0 ? 'text-success' : 'text-danger'
              return <span className={color}>${fmtDec(val)}</span>
          }},
        { id: 'break_even_acos', header: (
              <OverlayTrigger placement="top" overlay={
                  <Tooltip>Break-Even ACoS: the threshold at which ads become unprofitable. If your actual ACoS is below this, your ads are profitable.</Tooltip>
              }>
                  <span>BE ACoS <i className="fas fa-info-circle text-muted" style={{fontSize: '0.75rem'}}></i></span>
              </OverlayTrigger>
          ), sortable: true,
          cell: (row) => fmtPct(row.break_even_acos) },
        { id: 'max_profitable_cpa', header: 'Max CPA', sortable: true,
          cell: (row) => {
              if (row.max_profitable_cpa === null || row.max_profitable_cpa === undefined) return ''
              return `$${fmtDec(row.max_profitable_cpa)}`
          }},
        { id: 'return_on_cost', header: 'Return on Cost', sortable: true,
          cell: (row) => row.return_on_cost ? fmtPct(row.return_on_cost / 100) : '' },
        { id: 'units_sold', header: 'Units Sold', sortable: true,
          cell: (row) => fmtInt(row.units_sold) },
        { id: 'units_returned', header: 'Units Returned', sortable: true,
          cell: (row) => fmtInt(row.units_returned) },
        { id: 'revenue', header: 'Revenue', sortable: true,
          cell: (row) => row.revenue ? `$${fmtDec(row.revenue)}` : '' },
        { id: 'ad_spend_total', header: 'Spend', sortable: true,
          cell: (row) => row.ad_spend_total ? `$${fmtDec(row.ad_spend_total)}` : '' },
        { id: 'ad_sales', header: 'Sales (Ad)', sortable: true,
          cell: (row) => row.ad_sales ? `$${fmtDec(row.ad_sales)}` : '' },
        { id: 'ad_impressions', header: 'Impressions', sortable: true,
          cell: (row) => fmtInt(row.ad_impressions) },
        { id: 'ad_clicks', header: 'Clicks', sortable: true,
          cell: (row) => fmtInt(row.ad_clicks) },
        { id: 'acos', header: 'ACOS', sortable: true,
          cell: (row) => {
              const acos = fmtPct(row.acos)
              if (!acos) return ''
              if (row.break_even_acos !== null && row.break_even_acos !== undefined && row.acos !== null) {
                  const color = Number(row.acos) <= Number(row.break_even_acos) ? 'text-success' : 'text-danger'
                  return <span className={color}>{acos}</span>
              }
              return acos
          }},
        { id: 'ad_conversion_rate', header: 'Conversion (Ad)',
          cell: (row) => fmtPct(row.ad_conversion_rate) },
        { id: 'total_conversion_rate', header: 'Conversion (Total)', sortable: true,
          cell: (row) => fmtPct(row.total_conversion_rate) },
        { id: 'sessions', header: 'Sessions', sortable: true,
          cell: (row) => fmtInt(row.sessions) },
        { id: 'share_sales_by_ad', header: 'Share Sales by Ad', sortable: true,
          cell: (row) => fmtPct(row.share_sales_by_ad) },
        { id: 'ad_cost_share_of_price', header: 'Ad Cost in Price %',
          cell: (row) => fmtPct(row.ad_cost_share_of_price) },
        { id: 'available_qty', header: 'Available', sortable: true,
          cell: (row) => fmtInt(row.available_qty) },
        { id: 'total_fba_qty', header: 'Inventory at FBA', sortable: true,
          cell: (row) => fmtInt(row.total_fba_qty) },
        { id: 'month_supply', header: 'Month Supply', sortable: true,
          cell: (row) => row.month_supply ? fmtDec(row.month_supply, 1) : '' },
        { id: 'your_price', header: 'Your Price',
          cell: (row) => row.your_price ? `$${fmtDec(row.your_price)}` : '' },
        { id: 'fba_sale_price', header: 'Sale Price (FBA)', sortable: true,
          cell: (row) => row.fba_sale_price ? `$${fmtDec(row.fba_sale_price)}` : '' },
        { id: 'inventory_health_status', header: 'FBA Inventory Status', sortable: true,
          cell: (row) => {
              const status = row.inventory_health_status
              if (!status) return ''
              const colorMap = { 'Healthy': 'success', 'Excess': 'warning', 'Low stock': 'danger', 'Out of stock': 'danger' }
              const variant = Object.entries(colorMap).find(([k]) => status.toLowerCase().includes(k.toLowerCase()))?.[1] || 'secondary'
              return <span className={`badge bg-${variant}`}>{status}</span>
          }},
        { id: 'inventory_level', header: 'Inventory Level', sortable: true,
          cell: (row) => {
              if (!row.inventory_level) return ''
              const variantMap = { 'Out of stock': 'danger', 'Low stock': 'warning', 'Enough': 'success' }
              const variant = variantMap[row.inventory_level] || 'secondary'
              return <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>{row.inventory_level}</Badge>
          }},
        { id: 'low_inventory_fee', header: 'Low Inventory Fee',
          cell: (row) => row.low_inventory_fee ? <i className="fas fa-exclamation-circle text-warning" title="Low inventory fee applied"></i> : '' },
    ].filter(col => columnVisibility[col.id])

    if (reportLoading) {
        return (
            <Container fluid className="px-4 py-4">
                <div className="text-center py-4"><Spinner animation="border" /></div>
            </Container>
        )
    }

    const headerSubtitle = report
        ? `${report.seller_account_name || ''} (${formatDateOnly(report.start_date)} – ${formatDateOnly(report.end_date)})${report.report_type === 'monthly' ? ' — Monthly Profitability' : ''}`
        : ''

    const summary = report?.summary

    return (
        <Container fluid className="px-4 py-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <h1 className="mb-0">ASIN Profitability</h1>
                    {headerSubtitle && <p className="text-muted mb-0">{headerSubtitle}</p>}
                </div>
                <Button variant="outline-primary" onClick={() => navigate('/amazon-asin-report', { state: { returnFilters } })}>
                    <i className="fas fa-arrow-left me-2"></i>Back to List
                </Button>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}

            {rowsLoading && rows.length === 0 ? (
                <div className="text-center py-4"><Spinner animation="border" /></div>
            ) : (
                <>
                    {summary && (
                        <Row className="mb-4 g-3">
                            <Col><div className="bg-primary text-white p-3 rounded text-center"><h5 className="mb-1">Total ASINs</h5><h3 className="mb-0">{summary.total_asins}</h3></div></Col>
                            <Col><div className="bg-success text-white p-3 rounded text-center"><h5 className="mb-1">Total Revenue</h5><h3 className="mb-0">{formatCurrency(summary.total_revenue)}</h3></div></Col>
                            <Col><div className="bg-info text-white p-3 rounded text-center"><h5 className="mb-1">Total Units Sold</h5><h3 className="mb-0">{(summary.total_units_sold || 0).toLocaleString()}</h3></div></Col>
                            <Col><div className="bg-warning text-white p-3 rounded text-center"><h5 className="mb-1">Total Ad Spend</h5><h3 className="mb-0">{formatCurrency(summary.total_ad_spend)}</h3></div></Col>
                            <Col><div className="bg-secondary text-white p-3 rounded text-center"><h5 className="mb-1">Avg Profit</h5><h3 className="mb-0">{formatCurrency(summary.avg_profit)}</h3></div></Col>
                            <Col><div className="bg-danger text-white p-3 rounded text-center"><h5 className="mb-1">TACoS</h5><h3 className="mb-0">{summary.tacos != null ? `${summary.tacos}%` : ''}</h3></div></Col>
                        </Row>
                    )}
                    <div className="d-flex align-items-center mb-3">
                        <Button variant="info" onClick={handleExport} disabled={exporting} className="me-2">
                            <i className="bi bi-file-earmark-excel me-2"></i>{exporting ? 'Exporting...' : 'Export Excel'}
                        </Button>
                        <div>{createColumnToggleDropdown(columnVisibility, columnLabels, toggleColumnVisibility)}</div>
                        <Form.Check type="checkbox" label="Exclude ASINs with no sales"
                            checked={excludeNoSales} onChange={(e) => { setExcludeNoSales(e.target.checked); setCurrentPage(1) }}
                            className="ms-3" style={{ fontSize: '0.85rem' }} />
                        <div className="ms-auto" style={{ maxWidth: '300px' }}>
                            <SearchInputWithClear value={searchInputValue} onChange={handleSearch}
                                onClear={handleClearSearch} placeholder="Search ASIN, name, or SKU..." />
                        </div>
                    </div>
                    {rows.length === 0 ? (
                        <p className="text-muted">No profitability data found.</p>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <ResizableTable columns={reportColumns} data={rows} tableId="amazon_asin_report"
                                    enableSorting={true} onSort={handleSort}
                                    sortColumn={sortColumn} sortDirection={sortDirection}
                                    initialColumnWidths={{
                                        id: 60, asin: 130, product_name: 250, amazon_sku: 110,
                                        sale_price: 90, fba_fee: 90, inbound_fee: 90, referral_fee: 100,
                                        storage_fee: 90, awd_fee: 80, ad_spend_per_unit: 110,
                                        work_cost: 80, cogs: 70, profit: 80,
                                        contribution_margin: 120, break_even_acos: 95, max_profitable_cpa: 100,
                                        return_on_cost: 110,
                                        units_sold: 90, units_returned: 110, revenue: 100,
                                        ad_spend_total: 90, ad_sales: 90, ad_impressions: 100,
                                        ad_clicks: 70, acos: 70, ad_conversion_rate: 120,
                                        total_conversion_rate: 130, sessions: 90,
                                        share_sales_by_ad: 120, ad_cost_share_of_price: 130,
                                        available_qty: 80, total_fba_qty: 110,
                                        month_supply: 100, your_price: 90,
                                        fba_sale_price: 110, inventory_health_status: 140,
                                        low_inventory_fee: 130,
                                    }}
                                    striped hover className="table-sm" />
                            </div>
                            <div className="mt-3">
                                <PaginationComponent
                                    currentPage={currentPage}
                                    totalItems={totalItems}
                                    onPageChange={setCurrentPage}
                                    onItemsPerPageChange={(size) => { setPageSize(size); setCurrentPage(1) }}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
        </Container>
    )
}

export default AmazonASINReportDetailScreen
